import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/useAuth";
import * as api from "@/lib/api";

export function useWriterDashboard() {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["writerDashboard"],
    queryFn: () => {
      const token = getToken();
      if (!token) throw new Error("Not authenticated");
      return api.getWriterDashboard(token);
    },
  });
}

export function useWriterArticle(id: string) {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["writerArticle", id],
    queryFn: () => {
      const token = getToken();
      if (!token) throw new Error("Not authenticated");
      return api.getWriterArticle(token, id);
    },
    enabled: !!id,
  });
}

export function useWriterArticles(limit = 50) {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["writerArticles", limit],
    queryFn: () => {
      const token = getToken();
      if (!token) throw new Error("Not authenticated");
      return api.listWriterArticles(token, limit);
    },
  });
}

export function useOnboardWriter() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { displayName: string; bio: string }) => {
      const token = getToken();
      if (!token) throw new Error("Not authenticated");
      return api.onboardWriter(token, data.displayName, data.bio);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["writerDashboard"] });
    },
  });
}

export function useUpdateWriterProfile() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      displayName: string;
      tagline: string;
      bio: string;
      socialLinks: Record<string, string>;
    }) => {
      const token = getToken();
      if (!token) throw new Error("Not authenticated");
      return api.updateWriterProfile(token, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["writerDashboard"] });
    },
  });
}

export function useCreateArticle() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      title: string;
      subtitle: string;
      coverImageUrl: string;
      contentMarkdown: string;
      previewMarkdown: string;
      priceUsdc: string;
      priceEurc: string;
      category: string;
      topicNames: string[];
    }) => {
      const token = getToken();
      if (!token) throw new Error("Not authenticated");
      return api.createArticle(token, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["writerArticles"] });
    },
  });
}

export function useUpdateArticle() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      id: string;
      title: string;
      subtitle: string;
      coverImageUrl: string;
      contentMarkdown: string;
      previewMarkdown: string;
      priceUsdc: string;
      priceEurc: string;
      category: string;
      topicNames: string[];
    }) => {
      const token = getToken();
      if (!token) throw new Error("Not authenticated");
      const { id, ...rest } = data;
      return api.updateArticle(token, id, rest);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["writerArticles"] });
    },
  });
}

export function useSetArticlePublished() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string; published: boolean }) => {
      const token = getToken();
      if (!token) throw new Error("Not authenticated");
      return data.published
        ? api.publishArticle(token, data.id)
        : api.unpublishArticle(token, data.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["writerArticles"] });
      queryClient.invalidateQueries({ queryKey: ["writerDashboard"] });
      queryClient.invalidateQueries({ queryKey: ["articles"] });
    },
  });
}
